import { can, describeDue, dueBucket, isDay, localDay } from '@quickreport/domain';
import { kvGet, kvSet, type LocalDb, queueChange, type Tx } from '@quickreport/sync';
import { recordAudit } from './audit';
import { WorkflowError, type WorkspaceCtx } from './reports';

/** A report that should be ready today, or was due earlier and is still not done. */
export interface Reminder {
  /** Stable key for "read": it changes when the due date changes, so a delayed report speaks again. */
  key: string;
  reportId: string;
  labNo: string;
  name: string;
  patient: string;
  status: string;
  dueDate: string;
  bucket: 'overdue' | 'today';
  /** "today", "yesterday", "3 days ago (2 Oct)". */
  when: string;
}

/**
 * Reports promised for today or earlier that nobody has finished: not signed, not revoked and
 * not marked complete. Works on this computer's own data, so it works offline.
 */
export async function listReminders(
  db: Pick<LocalDb, 'query'> | Tx,
  today: string = localDay(),
): Promise<Reminder[]> {
  const r = await db.query<{
    id: string;
    name: string;
    lab_no: string;
    status: string;
    due_date: string;
    patient: string | null;
  }>(
    `select id, name, lab_no, status, due_date::text as due_date,
            content->'patient'->>'name' as patient
       from reports
      where deleted_at is null and due_date is not null and due_date <= $1::date
        and due_done_at is null and status not in ('final', 'revoked')
      order by due_date, lab_no
      limit 500`,
    [today],
  );
  return r.rows.map((x) => ({
    key: `due:${x.id}:${x.due_date}`,
    reportId: x.id,
    labNo: x.lab_no,
    name: x.name,
    patient: x.patient ?? '',
    status: x.status,
    dueDate: x.due_date,
    bucket: dueBucket(x.due_date, today) === 'overdue' ? 'overdue' : 'today',
    when: describeDue(x.due_date, today),
  }));
}

function canHandle(ctx: WorkspaceCtx) {
  return can(ctx.roles, 'register_visit') || can(ctx.roles, 'enter_results');
}

/** The lab says the work is finished, without waiting for a signature. */
export async function completeReminder(
  db: LocalDb,
  ctx: WorkspaceCtx,
  reportId: string,
): Promise<void> {
  if (!canHandle(ctx)) throw new WorkflowError('Your role cannot change a report’s due date.');
  await db.transaction(async (tx) => {
    const r = await tx.query<{ due_date: string | null }>(
      'select due_date::text as due_date from reports where id = $1 and deleted_at is null',
      [reportId],
    );
    if (!r.rows[0]) throw new WorkflowError('That report no longer exists.');
    await tx.query('update reports set due_done_at = now(), updated_at = now() where id = $1', [
      reportId,
    ]);
    await queueChange(tx, 'reports', reportId);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'report',
      entityId: reportId,
      action: 'due_completed',
      before: { dueDate: r.rows[0].due_date },
    });
  });
}

/** The report will be late: give it a new date. It reminds again on that day. */
export async function delayReport(
  db: LocalDb,
  ctx: WorkspaceCtx,
  reportId: string,
  newDay: string,
  today: string = localDay(),
): Promise<void> {
  if (!canHandle(ctx)) throw new WorkflowError('Your role cannot change a report’s due date.');
  if (!isDay(newDay)) throw new WorkflowError('Choose the new date.');
  if (newDay <= today) throw new WorkflowError('Choose a date after today.');
  await db.transaction(async (tx) => {
    const r = await tx.query<{ due_date: string | null }>(
      'select due_date::text as due_date from reports where id = $1 and deleted_at is null',
      [reportId],
    );
    if (!r.rows[0]) throw new WorkflowError('That report no longer exists.');
    await tx.query(
      'update reports set due_date = $2::date, due_done_at = null, updated_at = now() where id = $1',
      [reportId, newDay],
    );
    await queueChange(tx, 'reports', reportId);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'report',
      entityId: reportId,
      action: 'due_delayed',
      before: { dueDate: r.rows[0].due_date },
      after: { dueDate: newDay },
    });
  });
}

/** Set or change the due date from the report page. */
export async function setDueDate(
  db: LocalDb,
  ctx: WorkspaceCtx,
  reportId: string,
  day: string,
): Promise<void> {
  if (!canHandle(ctx)) throw new WorkflowError('Your role cannot change a report’s due date.');
  if (!isDay(day)) throw new WorkflowError('Choose a date.');
  await db.transaction(async (tx) => {
    const r = await tx.query<{ due_date: string | null; done: boolean }>(
      'select due_date::text as due_date, due_done_at is not null as done from reports where id = $1 and deleted_at is null',
      [reportId],
    );
    const cur = r.rows[0];
    if (!cur) throw new WorkflowError('That report no longer exists.');
    // The same day and nothing marked done: nothing to change.
    if (cur.due_date === day && !cur.done) return;
    await tx.query(
      'update reports set due_date = $2::date, due_done_at = null, updated_at = now() where id = $1',
      [reportId, day],
    );
    await queueChange(tx, 'reports', reportId);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'report',
      entityId: reportId,
      action: 'due_delayed',
      before: { dueDate: cur.due_date },
      after: { dueDate: day },
    });
  });
}

// ---- which notifications this person has read ---------------------------------------------
// Kept on this computer only: reading is personal, and it must work offline.

const READ_KEY = 'notifications_read';

export async function readKeys(db: Pick<LocalDb, 'query'> | Tx): Promise<Set<string>> {
  try {
    const raw = await kvGet(db as LocalDb, READ_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

export async function markRead(db: LocalDb, keys: string[]): Promise<void> {
  const have = await readKeys(db);
  for (const k of keys) have.add(k);
  // Keep the list from growing for ever: the newest 2000 are plenty.
  await kvSet(db, READ_KEY, JSON.stringify([...have].slice(-2000)));
}

export async function markUnread(db: LocalDb, keys: string[]): Promise<void> {
  const have = await readKeys(db);
  for (const k of keys) have.delete(k);
  await kvSet(db, READ_KEY, JSON.stringify([...have]));
}
